import { useState } from "react"
import { pimWorkflowListQuerySchema } from "@nova-ai/shared"
import { X } from "lucide-react"

import { formatDateTime } from "../../lib/format-date"
import type { WorkflowHistorySearch } from "../../services/use-pim-workflow-history"
import { Button } from "../ui/button"
import { Combobox } from "../ui/combobox"
import { FormField } from "../ui/form-field"
import { TextInput } from "../ui/text-input"
import { SOURCE_KIND_OPTIONS, workflowKindLabel } from "./workflow-history-presentation"

type Props = { search: WorkflowHistorySearch; onChange: (search: WorkflowHistorySearch) => void }
const fields = [
  { name: "sku", label: "Exact SKU", type: "text" },
  { name: "productId", label: "Product ID", type: "number" },
  { name: "store", label: "Store", type: "text" },
  { name: "from", label: "Received from (local time)", type: "datetime-local" },
  { name: "to", label: "Received until (local time)", type: "datetime-local" },
] as const
const hiddenFields = [
  ["productId", "Product ID"],
  ["store", "Store"],
  ["kind", "Kind"],
  ["from", "From"],
  ["to", "Until"],
] as const

function localDateInput(value: string | undefined) {
  if (!value) {
    return ""
  }
  const date = new Date(value)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

export function WorkflowHistoryFilters({ search, onChange }: Props) {
  const [status, setStatus] = useState<string | undefined>(search.status)
  const [kind, setKind] = useState<string | undefined>(search.kind)
  const [isExpanded, setIsExpanded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const kindOptions =
    kind && !SOURCE_KIND_OPTIONS.some((option) => option.value === kind)
      ? [...SOURCE_KIND_OPTIONS, { value: kind, label: workflowKindLabel(kind) }]
      : SOURCE_KIND_OPTIONS
  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault()
        const form = new FormData(event.currentTarget)
        const values = Object.fromEntries(
          fields.map(({ name, type }) => {
            const value = String(form.get(name) ?? "").trim()
            return [
              name,
              value && type === "datetime-local"
                ? new Date(value).toISOString()
                : value || undefined,
            ]
          })
        )
        const parsed = pimWorkflowListQuerySchema.safeParse({
          ...values,
          status,
          kind,
          limit: search.limit,
        })
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? "Check the filters.")
          return
        }
        setError(null)
        onChange({ ...parsed.data, ...(search.receiptId ? { receiptId: search.receiptId } : {}) })
      }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <FilterInput field={fields[0]} search={search} />

        <FormField id="history-status" label="Workflow status">
          {(controlProps) => (
            <Combobox
              {...controlProps}
              id="history-status"
              value={status}
              onChange={setStatus}
              clearLabel="All statuses"
              placeholder="All statuses"
              options={[
                { value: "pending", label: "Pending, retrying or paused" },
                { value: "failed", label: "Failed" },
                { value: "unknown", label: "Unknown evidence" },
              ]}
            />
          )}
        </FormField>

        <Button type="submit" variant="primary">
          Apply filters
        </Button>

        <Button
          aria-expanded={isExpanded}
          aria-controls="history-more-filters"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Fewer filters" : "More filters"}
        </Button>

        <Button
          onClick={() =>
            onChange({
              limit: search.limit,
              ...(search.receiptId ? { receiptId: search.receiptId } : {}),
            })
          }
        >
          Clear filters
        </Button>
      </div>

      <div id="history-more-filters" hidden={!isExpanded}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {fields
            .filter(({ name }) => name !== "sku")
            .map((field) => (
              <FilterInput key={field.name} field={field} search={search} />
            ))}

          <FormField id="history-kind" label="Kind">
            {(controlProps) => (
              <Combobox
                {...controlProps}
                id="history-kind"
                value={kind}
                onChange={setKind}
                clearLabel="All kinds"
                placeholder="All kinds"
                options={kindOptions}
              />
            )}
          </FormField>
        </div>
      </div>

      <ActiveFilters search={search} onChange={onChange} />

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </form>
  )
}

function FilterInput({
  field,
  search,
}: {
  field: (typeof fields)[number]
  search: WorkflowHistorySearch
}) {
  const { name, label, type } = field
  return (
    <FormField id={`history-${name}`} label={label}>
      {(controlProps) => (
        <TextInput
          {...controlProps}
          id={`history-${name}`}
          name={name}
          type={type}
          min={type === "number" ? 1 : undefined}
          defaultValue={
            type === "datetime-local"
              ? localDateInput(String(search[name] ?? ""))
              : (search[name] ?? "")
          }
        />
      )}
    </FormField>
  )
}

function ActiveFilters({ search, onChange }: Props) {
  return (
    <div className="flex flex-wrap gap-2">
      {hiddenFields.map(([key, label]) => {
        const value = search[key]
        if (value === undefined) {
          return null
        }
        const display = filterValueLabel(key, value)
        return (
          <Button
            key={key}
            size="sm"
            aria-label={`Remove ${label}: ${display}`}
            onClick={() =>
              onChange({
                ...search,
                [key]: undefined,
                before: undefined,
                targetAfter: undefined,
                attemptAfter: undefined,
              })
            }
          >
            {`${label}: ${display}`}

            <X aria-hidden="true" className="size-3" />
          </Button>
        )
      })}
    </div>
  )
}

function filterValueLabel(key: (typeof hiddenFields)[number][0], value: string | number) {
  if (key === "kind") {
    return workflowKindLabel(String(value))
  }
  if (key === "from" || key === "to") {
    return formatDateTime(String(value))
  }
  return String(value)
}
